/**
 * Has this bidder placed a bid before, in this browser?
 *
 * Answers the question the first-bid primer asks. The DECISION lives in
 * `@auctisync/shared` (`showsFirstBidPrimer`) so the website and the phone show
 * the primer on the same terms; only the storage differs, and it should — the
 * app has device preferences, a browser has `localStorage`.
 *
 * 🔴 UNKNOWN MEANS "DO NOT SHOW IT". `localStorage` throws outright in some
 * contexts (a locked-down browser, some private modes) and comes back empty in
 * others, so a failure to read must not turn into a primer that reappears on
 * every bid forever. The standing footnote beside it says the same thing in
 * ordinary words, so the cost of being wrong in this direction is a plainer
 * sentence — not a bidder left uninformed.
 *
 * PER BROWSER, NOT PER ACCOUNT, and deliberately so. The primer teaches what a
 * maximum is at the moment it first matters; someone bidding from a new browser
 * is well served by seeing it again, and storing it per account would mean a
 * read before we can draw the panel.
 */
import { showsFirstBidPrimer, type FirstBidState } from '@auctisync/shared';
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'auctisync.bidder.hasBid';

function readState(): FirstBidState {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1' ? 'yes' : 'no';
  } catch {
    // Storage refused to answer. See the note above: unknown is not "no".
    return 'unknown';
  }
}

export interface UseFirstBidResult {
  /** Draw the primer in place of the panel's standing footnote. */
  showPrimer: boolean;
  /** Call when a bid is ACCEPTED — never when one is merely attempted. */
  markFirstBid: () => void;
}

export function useFirstBid(): UseFirstBidResult {
  // Starts `unknown` rather than reading during render: the first paint on a
  // server-rendered or hydrated page must not depend on storage, and unknown
  // is the safe direction anyway.
  const [state, setState] = useState<FirstBidState>('unknown');

  useEffect(() => {
    setState(readState());
  }, []);

  const markFirstBid = useCallback(() => {
    setState('yes');
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // A bidder who cannot persist this sees the primer again next time. That
      // is the harmless direction, and it is not worth failing a placed bid over.
    }
  }, []);

  return { showPrimer: showsFirstBidPrimer(state), markFirstBid };
}
